import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search } from "lucide-react";

const STATUS_STYLE = {
  new: "bg-blue-50 text-blue-700",
  in_review: "bg-yellow-50 text-yellow-700",
  accepted: "bg-emerald-50 text-emerald-700",
  declined: "bg-red-50 text-red-700",
  closed: "bg-gray-100 text-gray-600",
};
const URGENCY_STYLE = {
  crisis: "bg-red-50 text-red-700 border border-red-200",
  high: "bg-orange-50 text-orange-700",
  standard: "bg-njas-sage-tint text-njas-sage-hover",
  low: "bg-gray-50 text-gray-500",
};
const STATUS_LABEL = { new: "Pending", in_review: "In Review", accepted: "Accepted", declined: "Declined", closed: "Closed" };

export default function Referrals() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = status === "all" ? {} : { status_filter: status };
    api.get("/referrals", { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [status]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.reference_code, r.client_first_name, r.client_last_name, r.referrer_name, r.reason]
        .filter(Boolean).some((v) => v.toLowerCase().includes(s))
    );
  }, [q, rows]);

  return (
    <div className="fade-in" data-testid="referrals-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">CRM</div>
          <h2 className="font-serif text-3xl text-njas-navy">Referrals</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-njas-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, ref, reason…"
              data-testid="referrals-search"
              className="pl-9 pr-4 py-2.5 rounded-lg border border-[var(--njas-border)] bg-white text-sm w-72 focus:outline-none focus:ring-2 focus:ring-njas-sage"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="referrals-status-filter" className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2.5 px-3">
            <option value="all">All statuses</option>
            <option value="new">Pending review</option>
            <option value="in_review">In Review</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">Ref</th>
                <th className="text-left px-5 py-3">Client</th>
                <th className="text-left px-5 py-3">Type</th>
                <th className="text-left px-5 py-3">Urgency</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Assigned</th>
                <th className="text-left px-5 py-3">Received</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-njas-muted">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-njas-muted">No referrals yet.</td></tr>
              )}
              {!loading && filtered.map((r) => (
                <tr key={r.id} className="border-t border-[var(--njas-border)] hover:bg-njas-bg-alt/60">
                  <td className="px-5 py-4 font-mono text-xs text-njas-navy">
                    <Link to={`/dashboard/referrals/${r.id}`} className="njas-link" data-testid={`ref-row-${r.id}`}>
                      {r.reference_code}
                    </Link>
                  </td>
                  <td className="px-5 py-4 text-njas-text">{r.client_first_name} {r.client_last_name}</td>
                  <td className="px-5 py-4 text-njas-muted capitalize">{r.referral_type?.replace("_", " ")}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium capitalize ${URGENCY_STYLE[r.urgency] || URGENCY_STYLE.standard}`}>{r.urgency}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLE[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</span>
                  </td>
                  <td className="px-5 py-4 text-njas-muted">{r.assigned_to_name || <span className="text-njas-muted/60">—</span>}</td>
                  <td className="px-5 py-4 text-njas-muted">{new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
